import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getFirestore, doc, onSnapshot, getDoc } from "firebase/firestore";
import app from "../../firebase";
import { clearEnrolledSession } from "../../auth/roleSession";
import { buildEnrollmentWindows, formatCalendarDate, useCalendarClock } from "../../utils/announcementDates";

export default function PaymentOfFees({ student: propStudent }) {
  const todayKey = useCalendarClock();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [student, setStudent] = useState(propStudent || state?.student || null);
  const [loading, setLoading] = useState(true);

  const [fees, setFees] = useState([]);
  const [cashDiscount, setCashDiscount] = useState(0);
  const [cashWithDiscountDate, setCashWithDiscountDate] = useState("");
  const [cashWithoutDiscountDate, setCashWithoutDiscountDate] = useState("");
  const [installmentDates, setInstallmentDates] = useState([]);
  const [announcementSettings, setAnnouncementSettings] = useState(null);

  const SIDEBAR_WIDTH = 240;
  const HEADER_HEIGHT = 70;
  const PAGE_PADDING = 32;
  const CONTENT_MAX = 1100;
  const [scrollOffset, setScrollOffset] = useState(0);
  const [lastScrollTop, setLastScrollTop] = useState(0);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const fullName = `${(student?.firstName || "")} ${(student?.lastName || "")}`.trim();
  const profilePicUrl = (student?.profilePicUrl) || `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName || "Student")}`;

  const navItems = [
    { key: "home", label: "Home", path: "/enrolled/dashboard" },
    { key: "pre", label: "Pre-Registration", path: "/enrolled/pre-registration" },
    { key: "curriculum", label: "Curriculum Overview", path: "/enrolled/curriculum-overview" },
    { key: "schedule", label: "Class Schedule", path: "/enrolled/class-schedule" },
    { key: "final", label: "COR", path: "/enrolled/cor" },
  ];

  useEffect(() => {
    if (student?.id) {
      const db = getFirestore(app);
      const studentRef = doc(db, "students", student.id);
      const unsubscribe = onSnapshot(studentRef, (snap) => {
        if (snap.exists()) {
          setStudent({ id: snap.id, ...snap.data() });
        }
        setLoading(false);
      });
      return () => unsubscribe();
    } else {
      setLoading(false);
    }
  }, [student?.id]);

  useEffect(() => {
    const db = getFirestore(app);
    const ref = doc(db, "systemSettings", "announcement");
    const unsubscribe = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        setAnnouncementSettings(snap.data());
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchFees = async () => {
      try {
        const db = getFirestore(app);
        const feesRef = doc(db, "systemSettings", "miscellaneousFees");
        const feesSnap = await getDoc(feesRef);
        if (feesSnap.exists()) {
          const feesData = feesSnap.data();
          setFees(feesData.fees || []);
          setCashDiscount(feesData.cashDiscount || 0);
          setCashWithDiscountDate(feesData.cashWithDiscountDate || "");
          setCashWithoutDiscountDate(feesData.cashWithoutDiscountDate || "");
          setInstallmentDates(feesData.installmentDates || []);
        }
      } catch (err) {
        console.error("Failed to fetch miscellaneous fees:", err);
      }
    };

    fetchFees();
  }, []);

  const formatDate = (dateStr) => {
    return formatCalendarDate(dateStr) || "N/A";
  };

  const enrolledWindowInfo = buildEnrollmentWindows(announcementSettings || {}, todayKey);
  const isEnrolledWindowOpen = !!enrolledWindowInfo.active;
  const windowForMessage = enrolledWindowInfo.active || enrolledWindowInfo.next;

  const termKey = useMemo(() => {
    if (state?.termKey) return state.termKey;
    const termSchedules = student?.termSchedules || {};
    const keys = Object.keys(termSchedules);
    if (keys.length === 0) return "";
    return keys.sort().pop() || "";
  }, [state?.termKey, student?.termSchedules]);

  const rawSchedules = useMemo(() => {
    if (!student || !termKey) return [];
    const termSchedules = student.termSchedules || {};
    return termSchedules[termKey] || [];
  }, [student, termKey]);

  const grouped = useMemo(() => {
    const map = {};
    rawSchedules.forEach((s) => {
      const key = `${s.courseId}|${s.section}|${s.semester}|${s.schoolYear}`;
      if (!map[key]) {
        map[key] = {
          courseId: s.courseId,
          courseName: s.courseName,
          section: s.section,
          units: s.units,
          semester: s.semester,
          schoolYear: s.schoolYear,
          meetings: []
        };
      }
      map[key].meetings.push({
        day: s.day,
        time: s.time,
        room: s.room
      });
    });
    return Object.values(map);
  }, [rawSchedules]);

  const totalUnits = useMemo(() => {
    return grouped.reduce((sum, course) => sum + (parseFloat(course.units) || 0), 0);
  }, [grouped]);

  const { feeRows, totalFees } = useMemo(() => {
    let total = 0;
    const rows = fees.map((fee, idx) => {
      let amount = 0;
      let formula = "";
      if (
        fee.formula &&
        fee.formula.toLowerCase().includes("units") &&
        typeof fee.amount === "number"
      ) {
        amount = totalUnits * fee.amount;
        formula = `(${totalUnits} units X ${fee.amount})`;
      } else if (fee.perUnit !== undefined) {
        amount = totalUnits * fee.perUnit;
        formula = `(${totalUnits} units X ${fee.perUnit})`;
      } else if (fee.formula) {
        formula = fee.formula;
        amount = fee.amount || 0;
      } else {
        amount = fee.amount || 0;
      }
      total += amount;
      return (
        <tr key={idx}>
          <td>
            {fee.label}
            {formula && (
              <span style={{ color: "#888", fontSize: 13, marginLeft: 6 }}>
                {formula}
              </span>
            )}
          </td>
          <td style={{ textAlign: "right", borderBottom: "2px solid #bdbdbd", color: "#4b3c0a" }}>
            {amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </td>
        </tr>
      );
    });

    return { feeRows: rows, totalFees: total };
  }, [fees, totalUnits]);

  const totalWithDiscount = useMemo(() => {
    const discount = parseFloat(cashDiscount) || 0;
    return Math.max(totalFees - discount, 0);
  }, [totalFees, cashDiscount]);

  const installment = useMemo(() => {
    return parseFloat((totalFees / 5).toFixed(2));
  }, [totalFees]);

  const handleProfile = (action) => {
    setProfileMenuOpen(false);
    if (action === "logout") {
      clearEnrolledSession();
      navigate("/enrolled/signin");
      return;
    }
  };

  // Handle scroll for hide-on-scroll behavior
  useEffect(() => {
    const mainElement = document.querySelector('[data-scroll-container]');
    if (!mainElement) return;

    const handleScroll = () => {
      const scrollTop = mainElement.scrollTop;
      if (scrollTop > lastScrollTop) {
        setScrollOffset(Math.min(scrollTop - lastScrollTop, HEADER_HEIGHT + SIDEBAR_WIDTH));
      } else {
        setScrollOffset(Math.max(scrollTop - lastScrollTop, -(HEADER_HEIGHT + SIDEBAR_WIDTH)));
      }
      setLastScrollTop(scrollTop);
    };

    mainElement.addEventListener('scroll', handleScroll);
    return () => mainElement.removeEventListener('scroll', handleScroll);
  }, [lastScrollTop]);

  return (
    <div style={{ display: "flex", minHeight: "100vh", fontFamily: "Inter, Arial, sans-serif", background: "#efefef" }}>
      {/* Sidebar */}
      <aside style={{ width: SIDEBAR_WIDTH, background: "#0f5f3b", color: "#fff", display: "flex", flexDirection: "column", position: "fixed", height: "100vh", left: 0, top: 0, transform: `translateY(${Math.max(-scrollOffset, -HEADER_HEIGHT)}px)`, transition: "transform 0.3s ease", zIndex: 1000 }}>
        <div style={{ padding: "28px 24px", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
          <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: 1 }}>EnrollEase</div>
        </div>
        <nav style={{ paddingTop: 24, flex: 1 }}>
          {navItems.map((n) => {
            const isActive = n.key === "pre" || window.location.pathname.startsWith(n.path);
            return (
              <div
                key={n.key}
                onClick={() => navigate(n.path, { state: { student } })}
                style={{
                  padding: "14px 24px",
                  margin: "4px 16px",
                  borderRadius: 8,
                  cursor: "pointer",
                  background: isActive ? "#ff6f2e" : "transparent",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: 15,
                  transition: "all 0.2s",
                  boxShadow: isActive ? "0 2px 8px rgba(255,111,46,0.3)" : "none"
                }}
              >
                {n.label}
              </div>
            );
          })}
        </nav>
      </aside>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", marginLeft: SIDEBAR_WIDTH }}>
        <header style={{ height: HEADER_HEIGHT, background: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", padding: `0 ${PAGE_PADDING}px`, boxSizing: "border-box", borderBottom: "1px solid #e0e0e0", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", position: "sticky", top: 0, transform: `translateY(${-Math.min(scrollOffset, HEADER_HEIGHT)}px)`, transition: "transform 0.3s ease", zIndex: 999, position: "relative" }}>
          <div style={{ color: "#0f5f3b", fontWeight: 700, fontSize: 20 }}>Payment of Fees</div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ textAlign: "right" }}>
              <div style={{ color: "#0f5f3b", fontWeight: 600, fontSize: 15 }}>{fullName}</div>
            </div>
            <img
              src={profilePicUrl}
              alt="Profile"
              style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", cursor: "pointer", border: "3px solid #ff6f2e" }}
              onClick={() => setProfileMenuOpen(v => !v)}
            />
            {profileMenuOpen && (
              <div style={{ position: "absolute", top: HEADER_HEIGHT - 4, right: PAGE_PADDING, background: "#fff", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.13)", minWidth: 180, padding: "10px 0", zIndex: 100 }}>
                <div style={{ padding: "10px 24px", cursor: "pointer", color: "#ff6f2e", fontWeight: 600 }} onClick={() => handleProfile("logout")}>Log out</div>
              </div>
            )}
          </div>
        </header>

        <main style={{ flex: 1, padding: PAGE_PADDING, boxSizing: "border-box", overflowY: "auto", background: "#efefef", marginTop: 0 }} data-scroll-container>
          <div style={{ maxWidth: CONTENT_MAX, margin: "0 auto" }}>
            {/* Step Indicator */}
            <div style={{ display: "flex", gap: 24, marginBottom: 24 }}>
              {["CLEARANCE", "COURSE LINE-UP", "PAYMENT OF FEES"].map((stepName, idx) => (
                <div
                  key={stepName}
                  onClick={() => {
                    if (stepName === "CLEARANCE") navigate("/enrolled/pre-registration", { state: { student } });
                    else if (stepName === "COURSE LINE-UP") navigate("/enrolled/course-lineup", { state: { student } });
                    else if (stepName === "PAYMENT OF FEES" && isEnrolledWindowOpen) navigate("/enrolled/payment-of-fees", { state: { student } });
                  }}
                  style={{
                    flex: 1,
                    padding: "16px 24px",
                    background: stepName === "PAYMENT OF FEES" ? "#ff6f2e" : "#fff",
                    color: stepName === "PAYMENT OF FEES" ? "#fff" : "#0f5f3b",
                    borderRadius: 8,
                    fontWeight: 700,
                    textAlign: "center",
                    cursor: stepName === "PAYMENT OF FEES" && !isEnrolledWindowOpen ? "not-allowed" : "pointer",
                    opacity: stepName === "PAYMENT OF FEES" && !isEnrolledWindowOpen ? 0.6 : 1,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                  }}
                >
                  {idx + 1}. {stepName}
                </div>
              ))}
            </div>

            {!isEnrolledWindowOpen && windowForMessage && (
              <div style={{ background: "#fff3cd", border: "1px solid #ffeeba", borderRadius: 8, padding: 16, marginBottom: 16, color: "#856404" }}>
                Enrollment window is closed. Please check Pre-Registration for the schedule.
                <div style={{ marginTop: 6, fontSize: 13 }}>
                  Next window: AY {windowForMessage.year || "—"} {windowForMessage.semester} •{" "}
                  {formatDate(windowForMessage.start)} to {formatDate(windowForMessage.end)}
                </div>
              </div>
            )}

            {!isEnrolledWindowOpen ? null : (
              <>
                {loading ? (
                  <div style={{ textAlign: "center", padding: 48, color: "#666" }}>Loading payment data...</div>
                ) : !student ? (
                  <div style={{ background: "#fff", borderRadius: 8, padding: 24, color: "#b71c1c" }}>
                    Student information not found.
                  </div>
                ) : rawSchedules.length === 0 ? (
                  <div style={{ background: "#fff", borderRadius: 12, padding: 24, color: "#555" }}>
                    No submitted Course Line-up found. Please complete Course Line-up first.
                  </div>
                ) : (
                  <div style={{ background: "#fff", borderRadius: 12, padding: 24, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: "#0f5f3b", marginBottom: 12 }}>
                      Submitted Course Line-up {termKey ? `(${termKey.replace("_", " • ")})` : ""}
                    </div>

                    <div style={{ overflowX: "auto", border: "1px solid #ddd", borderRadius: 8, marginBottom: 16 }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff" }}>
                        <thead>
                          <tr style={{ background: "#0f5f3b", color: "#fff" }}>
                            <th style={{ padding: "12px", textAlign: "left", fontWeight: 700, fontSize: 12, borderRight: "1px solid #ddd" }}>Subject Code</th>
                            <th style={{ padding: "12px", textAlign: "left", fontWeight: 700, fontSize: 12, borderRight: "1px solid #ddd" }}>Subject Name</th>
                            <th style={{ padding: "12px", textAlign: "left", fontWeight: 700, fontSize: 12, borderRight: "1px solid #ddd" }}>Schedule / Room</th>
                            <th style={{ padding: "12px", textAlign: "center", fontWeight: 700, fontSize: 12 }}>Units</th>
                          </tr>
                        </thead>
                        <tbody>
                          {grouped.map((course, idx) => (
                            <tr key={idx} style={{ borderBottom: "1px solid #ddd" }}>
                              <td style={{ padding: "12px", borderRight: "1px solid #ddd", fontWeight: 600, fontSize: 13 }}>
                                {course.courseId}
                              </td>
                              <td style={{ padding: "12px", borderRight: "1px solid #ddd", fontSize: 13 }}>
                                {course.courseName}
                              </td>
                              <td style={{ padding: "12px", borderRight: "1px solid #ddd", fontSize: 12 }}>
                                {(course.meetings || []).map((m, i) => (
                                  <div key={i} style={{ marginBottom: 4 }}>
                                    <div>{m.day} {m.time}</div>
                                    <div style={{ color: "#666" }}>Room: {m.room}</div>
                                  </div>
                                ))}
                              </td>
                              <td style={{ padding: "12px", textAlign: "center", fontWeight: 600, fontSize: 13 }}>
                                {course.units}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div style={{ background: "#f8fafb", borderRadius: 8, padding: 16, border: "1px solid #e0e0e0", marginBottom: 24 }}>
                      <div style={{ fontSize: 14, marginBottom: 8 }}>
                        <b>Total Courses:</b> {grouped.length}
                      </div>
                      <div style={{ fontSize: 14 }}>
                        <b>Total Units:</b> {totalUnits}
                      </div>
                    </div>

                    {/* Miscellaneous Fees (from StudentFinance) */}
                    <div style={{ background: "#fff", borderRadius: 8, boxShadow: "0 1px 2px rgba(0,0,0,0.03)", border: "1px solid #e5e7eb", padding: 24 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 40, flexWrap: "wrap" }}>
                        <div style={{ flex: 1, minWidth: 280 }}>
                          <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 14, letterSpacing: 1 }}>MISCELLANEOUS FEES</div>
                          <table style={{ width: "100%", fontSize: 15, marginBottom: 18 }}>
                            <thead>
                              <tr style={{ borderBottom: "2px solid #222" }}>
                                <th style={{ textAlign: "left", fontWeight: 500 }}> </th>
                                <th style={{ textAlign: "right", fontWeight: 500 }}>Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {feeRows}
                              <tr>
                                <td style={{ fontWeight: 700, fontSize: 16, paddingTop: 10 }}>Total:</td>
                                <td style={{ textAlign: "right", fontWeight: 700, fontSize: 16, paddingTop: 10 }}>
                                  {totalFees.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </td>
                              </tr>
                            </tbody>
                          </table>

                          <div style={{ fontWeight: 700, color: "#18803b", fontSize: 16, margin: "20px 0 6px 0" }}>
                            OPTION 1: CASH PAYMENT WITH DISCOUNT
                            <span style={{ float: "right" }}>{totalWithDiscount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                          </div>
                          <div style={{ color: "#222", fontSize: 14, marginBottom: 14 }}>
                            If full payment is made on or before {cashWithDiscountDate ? formatDate(cashWithDiscountDate) : "N/A"}.
                          </div>

                          <div style={{ fontWeight: 700, color: "#18803b", fontSize: 16, margin: "20px 0 6px 0" }}>
                            OPTION 2: CASH PAYMENT WITHOUT DISCOUNT
                            <span style={{ float: "right" }}>{totalFees.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                          </div>
                          <div style={{ color: "#222", fontSize: 14, marginBottom: 14 }}>
                            If full payment is made after {cashWithDiscountDate ? formatDate(cashWithDiscountDate) : "N/A"}.
                          </div>
                        </div>

                        <div style={{ flex: 1, minWidth: 280 }}>
                          <div style={{ fontWeight: 700, color: "#18803b", fontSize: 16, marginBottom: 14 }}>OPTION 3: INSTALLMENT PAYMENT</div>
                          <table style={{ width: "100%", fontSize: 15, marginBottom: 18 }}>
                            <tbody>
                              {installmentDates.map((date, idx) => (
                                <tr key={idx}>
                                  <td>{["Downpayment", "1st Installment", "2nd Installment", "3rd Installment", "4th Installment"][idx]}</td>
                                  <td>{date ? formatDate(date) : "N/A"}</td>
                                  <td style={{ textAlign: "right" }}>{installment.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                </tr>
                              ))}
                              <tr>
                                <td style={{ fontWeight: 700, fontSize: 16, paddingTop: 10 }}>Total:</td>
                                <td></td>
                                <td style={{ textAlign: "right", fontWeight: 700, fontSize: 16, paddingTop: 10 }}>
                                  {totalFees.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </td>
                              </tr>
                            </tbody>
                          </table>

                          <div style={{ background: "#fff8f1", border: "1px solid #ff9800", borderRadius: 6, padding: 16, color: "#e67e22", fontWeight: 600, fontSize: 15, marginTop: 20 }}>
                            <div style={{ color: "#e67e22", fontWeight: 700, marginBottom: 8 }}>NOTE</div>
                            <div style={{ color: "#444", fontWeight: 400, fontSize: 14 }}>
                              Please check your Miscellaneous Fees breakdown carefully before proceeding.<br />
                              Once verified, you may proceed to the campus Finances Office to settle your payment.
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}